/**
 * Hypergeometric consistency math for Sorcery: Contested Realm decks.
 *
 * All probabilities assume a 3-spellbook / 3-site opening hand with no
 * mulligan, and model threshold supply strictly from the atlas (sites drawn).
 * Spellbook-based threshold sources and avatar rules text are not modeled.
 * the math stays conservative and transparent rather than trying to guess
 * at per-card effects.
 */
import { ELEMENT_ORDER, getCardElements, getCardZone, getSorceryRarityLabel } from './deckMetrics';
import { getOpeningHand } from '../game/avatarRules';

const OPENING_SPELLBOOK = 3;
const OPENING_ATLAS = 3;
const MAX_REACH_TURN = 5;

/**
 * Resolve the opening-hand sizes for a deck's chosen avatar. Falls back
 * to the rulebook default of 3+3 when no avatar is selected. Avatars
 * like Magician (7 spells, 0 sites), Pathfinder (3 spells, 0 sites),
 * and Spellslinger (4 spells, 3 sites) all surface here.
 */
export function resolveOpeningHandSizes(chosenCards) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const avatarCard = entries.find((e) => getCardZone(e) === 'avatar')?.card;
  const { spellbook, atlas } = getOpeningHand(avatarCard);
  return { spellbook, atlas };
}

const THRESHOLD_FIELDS = {
  Water: 'waterThreshold',
  Earth: 'earthThreshold',
  Fire: 'fireThreshold',
  Air: 'airThreshold',
};

/** Binomial coefficient C(n, k). Returns 0 for invalid arguments. */
export function choose(n, k) {
  if (k < 0 || k > n || n < 0) return 0;
  if (k === 0 || k === n) return 1;
  const kk = Math.min(k, n - k);
  let result = 1;
  for (let i = 0; i < kk; i += 1) {
    result = (result * (n - i)) / (i + 1);
  }
  return result;
}

/**
 * Hypergeometric PMF: probability of drawing exactly k successes when sampling
 * n items without replacement from a population of N that contains K successes.
 */
export function hypergeometric(N, K, n, k) {
  if (N <= 0 || n < 0 || k < 0 || K < 0) return 0;
  if (n > N || K > N) return 0;
  const numerator = choose(K, k) * choose(N - K, n - k);
  const denominator = choose(N, n);
  return denominator === 0 ? 0 : numerator / denominator;
}

/** P(X >= k) under the same hypergeometric distribution. */
export function hypergeometricAtLeast(N, K, n, k) {
  if (k <= 0) return 1;
  if (k > n || k > K) return 0;
  let sum = 0;
  const upper = Math.min(n, K);
  for (let i = k; i <= upper; i += 1) sum += hypergeometric(N, K, n, i);
  return Math.min(1, Math.max(0, sum));
}

function siteThresholdVector(card) {
  const v = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  if (!card) return v;
  for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
    const n = Number(card[field] || 0);
    if (n > 0) v[element] = n;
  }
  return v;
}

function spellbookMaxDemand(spellbookEntries) {
  const demand = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  for (const entry of spellbookEntries) {
    const card = entry?.card;
    if (!card) continue;
    for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
      const d = Number(card[field] || 0);
      if (d > demand[element]) demand[element] = d;
    }
  }
  return demand;
}

function targetTurnForElement(demand) {
  if (demand <= 1) return 1;
  if (demand === 2) return 2;
  if (demand === 3) return 3;
  if (demand === 4) return 4;
  return 5;
}

/**
 * Total element threshold summed across all sites in the atlas. A site
 * supplying waterThreshold=2 counts as 2 toward Water demand, which lets
 * us treat demand as a hypergeometric "successes needed" target.
 */
function atlasElementSupply(atlasEntries) {
  const supply = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  for (const entry of atlasEntries) {
    const v = siteThresholdVector(entry?.card);
    supply.Water += v.Water;
    supply.Earth += v.Earth;
    supply.Fire += v.Fire;
    supply.Air += v.Air;
  }
  return supply;
}

/**
 * Per-element / per-turn threshold reach matrix.
 *
 * Returns { Water: [t1..t5], Earth: [...], Fire: [...], Air: [...], demand, target }
 * where each cell is the probability the atlas has supplied at least the
 * element's max demand in play by the main phase of that turn.
 *
 * Sorcery rules the math encodes:
 *   1. You start with 3 sites drawn in your opening hand.
 *   2. On every turn you get one free draw (we assume optimal play.
 *      always pick atlas. Which gives the best-case upper bound).
 *   3. You play at most one site per turn via your Avatar's tap ability,
 *      so by main phase of turn N you have at most N sites in play,
 *      regardless of how many sites are in hand.
 *
 * The play-cap matters: a 4-pip threshold demand is unreachable by turn 3
 * no matter how deep your atlas is, because you've only played 3 sites.
 */
export function thresholdReachByTurn(chosenCards) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const spellbook = entries.filter((e) => getCardZone(e) === 'spellbook');
  const atlas = entries.filter((e) => getCardZone(e) === 'atlas');

  const demand = spellbookMaxDemand(spellbook);
  const supply = atlasElementSupply(atlas);

  const N = atlas.length;
  const result = { demand, target: {}, Water: [], Earth: [], Fire: [], Air: [] };

  for (const element of ELEMENT_ORDER) {
    const D = demand[element] || 0;
    result.target[element] = targetTurnForElement(D);
    const K = supply[element] || 0;

    for (let turn = 1; turn <= MAX_REACH_TURN; turn += 1) {
      let p;
      if (D === 0) {
        p = 1;
      } else if (turn < D) {
        // Site-play cap: max `turn` sites can be in play by turn N,
        // so a demand > turn is flat-out impossible regardless of draws.
        p = 0;
      } else if (N === 0 || K === 0) {
        p = 0;
      } else {
        // By main phase of turn N you've drawn OPENING_ATLAS + N sites
        // (opening hand + one free draw per turn, always picked atlas).
        const drawsByTurn = Math.min(N, OPENING_ATLAS + turn);
        p = hypergeometricAtLeast(N, K, drawsByTurn, D);
      }
      result[element].push(p);
    }
  }

  return result;
}

/**
 * Probability that the opening 3-spellbook hand contains a spell whose
 * threshold and cost requirements are met given the opening 3-site atlas.
 */
export function openingHandPlayability(chosenCards) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const spellbook = entries.filter((e) => getCardZone(e) === 'spellbook');
  const atlas = entries.filter((e) => getCardZone(e) === 'atlas');
  const openingSizes = resolveOpeningHandSizes(chosenCards);

  const S = spellbook.length;
  const A = atlas.length;
  // Only require deep-enough decks for the zones this avatar actually
  // uses. Magician has atlas=0 so we skip the atlas depth check.
  if (S < openingSizes.spellbook) {
    return { playablePct: 0, deadPct: 1, avgPlayableTurn: 0 };
  }
  if (openingSizes.atlas > 0 && A < openingSizes.atlas) {
    return { playablePct: 0, deadPct: 1, avgPlayableTurn: 0 };
  }

  const expectedSupply = atlasElementSupply(atlas);
  const openingSupplyFactor = A === 0 ? 0 : openingSizes.atlas / A;

  let playableSpells = 0;
  let totalCostAvailable = 0;
  for (const entry of spellbook) {
    const card = entry?.card;
    if (!card) continue;
    const cost = Number(card.cost);
    if (!Number.isFinite(cost)) continue;
    if (cost > 3) continue;
    let ok = true;
    for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
      const need = Number(card[field] || 0);
      if (need <= 0) continue;
      const expected = (expectedSupply[element] || 0) * openingSupplyFactor;
      if (expected < need - 0.25) {
        ok = false;
        break;
      }
    }
    if (ok) playableSpells += 1;
    totalCostAvailable += 1;
  }

  const draws = Math.min(S, openingSizes.spellbook);
  const playablePct = totalCostAvailable === 0
    ? 0
    : hypergeometricAtLeast(S, playableSpells, draws, 1);

  let costSum = 0;
  let costCount = 0;
  for (const entry of spellbook) {
    const cost = Number(entry?.card?.cost);
    if (Number.isFinite(cost)) {
      costSum += cost;
      costCount += 1;
    }
  }
  const avgCost = costCount === 0 ? 0 : costSum / costCount;
  const avgPlayableTurn = Math.max(1, Math.min(6, avgCost));

  return {
    playablePct,
    deadPct: Math.max(0, 1 - playablePct),
    avgPlayableTurn,
  };
}

function pickRandom(rng, pool, n) {
  const indices = pool.map((_, i) => i);
  const picked = [];
  for (let i = 0; i < n && indices.length > 0; i += 1) {
    const idx = Math.floor(rng() * indices.length);
    picked.push(pool[indices[idx]]);
    indices.splice(idx, 1);
  }
  return picked;
}

/**
 * Given a spellbook hand and atlas hand, determine whether any spell is
 * castable with the thresholds currently supplied by the atlas hand.
 * Returns { playable, reason }.
 */
export function evaluateHand(spellbookHand, atlasHand) {
  const supply = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  for (const entry of atlasHand) {
    const v = siteThresholdVector(entry?.card);
    supply.Water += v.Water;
    supply.Earth += v.Earth;
    supply.Fire += v.Fire;
    supply.Air += v.Air;
  }

  for (const entry of spellbookHand) {
    const card = entry?.card;
    if (!card) continue;
    const cost = Number(card.cost);
    if (!Number.isFinite(cost) || cost > OPENING_ATLAS) continue;
    let meets = true;
    for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
      const need = Number(card[field] || 0);
      if (need > (supply[element] || 0)) {
        meets = false;
        break;
      }
    }
    if (meets) {
      return {
        playable: true,
        reason: `Can cast ${card.name} by turn ${Math.max(1, cost)}. Thresholds met.`,
      };
    }
  }

  return { playable: false, reason: 'No castable spell in opener.' };
}

export function sampleOpeningHand(chosenCards, { rng = Math.random } = {}) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const spellbook = entries.filter((e) => getCardZone(e) === 'spellbook');
  const atlas = entries.filter((e) => getCardZone(e) === 'atlas');
  const { spellbook: sbCount, atlas: atCount } = resolveOpeningHandSizes(chosenCards);

  const spellbookHand = pickRandom(rng, spellbook, Math.min(sbCount, spellbook.length));
  const atlasHand = pickRandom(rng, atlas, Math.min(atCount, atlas.length));
  const { playable, reason } = evaluateHand(spellbookHand, atlasHand);

  return { spellbook: spellbookHand, atlas: atlasHand, playable, reason };
}

/**
 * Replace specific cards in the current opening hand with fresh draws
 * from the remaining deck (cards not currently in either hand). Returns
 * a new hand plus the recomputed verdict.
 *
 * `spellIndices` / `atlasIndices` are arrays of indices (into the current
 * hand arrays) to replace. Cards being replaced are considered "returned"
 * and are eligible to be drawn again on subsequent mulligans.
 */
export function mulliganHand(chosenCards, currentHand, {
  spellIndices = [], atlasIndices = [], rng = Math.random,
} = {}) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const spellbook = entries.filter((e) => getCardZone(e) === 'spellbook');
  const atlas = entries.filter((e) => getCardZone(e) === 'atlas');

  const spellIdxSet = new Set(spellIndices);
  const atlasIdxSet = new Set(atlasIndices);

  const currentSpells = currentHand.spellbook || [];
  const currentAtlas = currentHand.atlas || [];

  const keepSpells = currentSpells.filter((_, i) => !spellIdxSet.has(i));
  const keepAtlas = currentAtlas.filter((_, i) => !atlasIdxSet.has(i));

  const spellPool = spellbook.filter((entry) => !keepSpells.includes(entry));
  const atlasPool = atlas.filter((entry) => !keepAtlas.includes(entry));

  const newSpells = pickRandom(rng, spellPool, Math.min(spellIndices.length, spellPool.length));
  const newAtlas = pickRandom(rng, atlasPool, Math.min(atlasIndices.length, atlasPool.length));

  // Rebuild in the same positions. Replaced slots get a fresh draw,
  // everything else keeps its original reference. This lets the UI
  // animate only the cards that actually changed instead of remounting
  // the whole fan on every mulligan.
  let spellCursor = 0;
  let atlasCursor = 0;
  const spellbookHand = currentSpells.map((orig, i) =>
    (spellIdxSet.has(i) && spellCursor < newSpells.length ? newSpells[spellCursor++] : orig),
  );
  const atlasHand = currentAtlas.map((orig, i) =>
    (atlasIdxSet.has(i) && atlasCursor < newAtlas.length ? newAtlas[atlasCursor++] : orig),
  );

  const { playable, reason } = evaluateHand(spellbookHand, atlasHand);

  return { spellbook: spellbookHand, atlas: atlasHand, playable, reason };
}

export const OPENING_HAND = { spellbook: OPENING_SPELLBOOK, atlas: OPENING_ATLAS };
export const REACH_TURNS = MAX_REACH_TURN;

export { getCardElements, getCardZone, getSorceryRarityLabel };
